import { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Container, Button, Alert } from '@mui/material';
import {
    Payment as PaymentIcon,
    Delete as DeleteIcon,
    Replay as ReplayIcon,
} from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import {
    AppContext,
    SttTabs,
    SttFullDialog,
    SttChipPaymentStatus,
} from '../../sporttia/all';
import PaymentModalDetail from './PaymentModalDetail';
import PaymentModalChildren from './PaymentModalChildren';
import PaymentFormDialog from '../dialogs/PaymentFormDialog';
import BookingDialog from '../dialogs/bookingDialog/BookingDialog';
import { useInteractionsFiles, useLoader } from '../../lib/hooks';
import DataphonePaymentDialog from '../dialogs/DataphonePaymentDialog';
import POSStripePaymentDialog from '../stripe/POSStripePaymentDialog';
import constants from '../../config/constants';
import usePaymentsService from '../../services/PaymentsService';
import SttError from '../error/SttError';
import SttFullPageSpinner from '../../sporttia/spinners/SttFullPageSpinner';
import { getErrorMessage } from '../../lib/utils';
import ModalConfirmation from '../../layout/ModalConfirmation';
import type { Payment } from '../../types/models/Payment';
import { GetPayment200 } from '../../types/api/paths/Payment';
import { PosPaymentParams, PaymentForm } from '../../types/payment';
import CashdroControlDialog from '../dialogs/CashdroControlDialog';
import { isDataphonePayment } from './refundDataphoneVisibility';
import { filterPaymentPrivileges } from './paymentPrivileges';
import { usePermission } from '../../lib/usePermission';
import { PERMISSIONS } from '../../lib/permissions';

type PaymentModalProps = {
    idPayment: number;
    onPay?: (payment: Payment) => void;
    onDeleted?: (payment: Payment) => void;
    onRecovered?: (payment: Payment) => void;
    onRefund?: (payment: Payment) => void;
    onSave?: (payment: Payment) => void;
    onClose?: () => void;
    /**
     * When true, disables URL-based navigation. Defaults to true.
     * Only set to false when opening PaymentModal from the main Payments page
     * to enable direct URL access (e.g., /payments/123).
     * Keep as true (default) when opening from other contexts (user profile,
     * events, activities, groups, etc.) to prevent navigation issues with back button.
     */
    disableUrlNavigation?: boolean;
};

export default function PaymentModal({
    idPayment,
    onPay,
    onDeleted,
    onRecovered,
    onRefund,
    onSave,
    onClose,
    disableUrlNavigation = true,
}: PaymentModalProps) {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const { openFile } = useInteractionsFiles();
    const [openSelectPF, setOpenSelectPF] = useState(false);
    const [dialogBookingId, setDialogBookingId] = useState<number | null>(null);
    const [idPaymentModal, setIdPaymentModal] = useState(idPayment);
    const [posPaymentParams, setPosPaymentParams] =
        useState<PosPaymentParams | null>(null);

    // Open confirmation modal on delete or recover
    const [operationConfirmation, setOperationConfirmation] = useState<
        'delete' | 'recover'
    >();

    const queryClient = useQueryClient();

    const paymentsService = usePaymentsService();
    const getPaymentQuery = paymentsService.useGetPayment(idPaymentModal, {
        trash: true,
    });

    // Lo que el backend permite hacer con el pago, recortado con los permisos
    // v8 del operador: `accounting.modify` para cobrar, recuperar y devolver;
    // `accounting.delete` para borrar. Con los roles apagados, sin cambios.
    const { can } = usePermission();
    const paymentPrivileges = filterPaymentPrivileges(
        getPaymentQuery.data?.payment?.privileges,
        {
            canModify: can(PERMISSIONS.ACCOUNTING.MODIFY),
            canDelete: can(PERMISSIONS.ACCOUNTING.DELETE),
        },
    );

    const CASHDRO = cxt?.sc?.cashdroConnected;

    const [cashdroPayment, setCashdroPayment] = useState(false);
    const [cashdroPaymentAmount, setCashdroPaymentAmount] = useState(0);

    const deletePaymentMutation = paymentsService.useDeletePayment({
        onSuccess: (response: any) => {
            queryClient.invalidateQueries({
                queryKey: getPaymentQuery.queryKey,
            });

            onDeleted?.(response.payment);
        },
        onError: (error: any) => {
            cxt.showMessage('E', getErrorMessage(error));
        },
    });

    const recoverPaymentMutation = paymentsService.useRecoverPayment({
        onSuccess: (response: any) => {
            queryClient.invalidateQueries({
                queryKey: getPaymentQuery.queryKey,
            });

            onRecovered?.(response.payment);
        },
        onError: (error: any) => {
            cxt.showMessage('E', getErrorMessage(error));
        },
    });

    const collectPaymentMutation = paymentsService.useCollectPayment({
        onSuccess: (response: any) => {
            queryClient.invalidateQueries({
                queryKey: getPaymentQuery.queryKey,
            });

            setOpenSelectPF(false);

            const { tpv, payment: paymentResponse } = response;
            if (
                tpv?.payment?.paymentForm ===
                constants.payment.paymentForms.dataphoneConnected.name
            ) {
                // params for the POS dialog (datáfono físico)
                setPosPaymentParams({
                    amount: tpv.price ?? 0,
                    concept: tpv.concept ?? '',
                    idTpv: tpv.id ?? 0,
                    sc: {
                        id: tpv.sc?.id,
                        scName: tpv.sc?.short,
                        entityName: tpv.sc?.customer?.name,
                        address: tpv.sc?.address,
                        cif: tpv.sc?.customer?.cif,
                        phone: tpv.sc?.phonePublic,
                    },
                    returnParams: {
                        payment: paymentResponse,
                    },
                });
            } else if (paymentResponse) {
                onPay?.(paymentResponse);
            }
        },
        onError: (error: any) => {
            cxt.showMessage('E', getErrorMessage(error));
        },
    });

    const refundPaymentMutation = paymentsService.useRefundPayment({
        onSuccess: (response: any) => {
            queryClient.invalidateQueries({
                queryKey: getPaymentQuery.queryKey,
            });

            if (response.payment) {
                onRefund?.(response.payment);
            }
        },
        onError: (error: any) => {
            cxt.showMessage('E', getErrorMessage(error));
        },
    });

    const ticket = (type: number) => {
        openFile(
            `/payments/${idPaymentModal}.pdf?format=${type}`,
            'application/pdf',
            null,
            `${t('generic.ticket')}_${idPaymentModal}`,
        );
    };

    const openPayment = (paymentToOpen: Payment) => {
        setIdPaymentModal(paymentToOpen.id);
    };

    const [, mutationsLoader] = useLoader([
        collectPaymentMutation.status,
        recoverPaymentMutation.status,
        refundPaymentMutation.status,
        deletePaymentMutation.status,
    ]);

    const setupPaymentCollection = (paymentForm: PaymentForm) => {
        if (
            CASHDRO &&
            paymentForm === constants.payment.paymentForms.cashdro.name
        ) {
            setCashdroPayment(true);
            setCashdroPaymentAmount(getPaymentQuery?.data?.payment?.price || 0);
        } else {
            collectPayment(paymentForm);
        }
    };

    const collectPayment = (paymentForm: PaymentForm) => {
        collectPaymentMutation.mutate({
            id: idPaymentModal,
            params: {
                paymentForm,
            },
        });
    };

    // Do not show SttFullDialog since payment is loaded
    if (getPaymentQuery.isLoading) {
        return <SttFullPageSpinner />;
    }

    if (getPaymentQuery.isError) {
        return (
            <SttFullDialog open title={t('generic.error')} onClose={onClose}>
                <SttError heading={false} />
            </SttFullDialog>
        );
    }

    if (!getPaymentQuery.isSuccess) {
        return null;
    }

    return (
        <>
            {getPaymentQuery.data.payment.privileges && (
                <SttFullDialog
                    open
                    onClose={onClose}
                    urlPath={
                        disableUrlNavigation
                            ? undefined
                            : `/payments/${idPaymentModal}`
                    }
                    title={
                        <Box>
                            {t('payment.payment')}
                            &nbsp;&nbsp;&nbsp;
                            <SttChipPaymentStatus
                                status={getPaymentQuery.data.payment.status}
                            />
                        </Box>
                    }
                    buttons={[
                        {
                            show: paymentPrivileges.includes(
                                constants.payment.privileges.pay,
                            ),
                            caption: t('generic.collect'),
                            onClick: () => setOpenSelectPF(true),
                            icon: <PaymentIcon />,
                        },
                    ]}
                    menu={
                        !getPaymentQuery.data.payment.parent ||
                        paymentPrivileges.includes(
                            constants.payment.privileges.refund,
                        )
                            ? [
                                  {
                                      caption: t('generic.recover'),
                                      icon: <ReplayIcon />,
                                      show: paymentPrivileges.includes(
                                          constants.payment.privileges.recover,
                                      ),
                                  },
                                  {
                                      caption: t('generic.delete'),
                                      onClick: () => {
                                          setOperationConfirmation('delete');
                                      },
                                      icon: <DeleteIcon />,
                                      show: paymentPrivileges.includes(
                                          constants.payment.privileges.delete,
                                      ),
                                  },
                                  {
                                      caption: `${t('generic.refund')} - ${t(
                                          'payment.paymentFormCash',
                                      )}`,
                                      divider: true,
                                      onClick: () => {
                                          refundPaymentMutation.mutate({
                                              id: idPaymentModal,
                                              params: {
                                                  paymentForm:
                                                      constants.payment
                                                          .paymentForms.cash
                                                          .name,
                                              },
                                          });
                                      },
                                      show: paymentPrivileges.includes(
                                          constants.payment.privileges.refund,
                                      ),
                                  },
                                  {
                                      caption: `${t('generic.refund')} - ${t(
                                          'payment.paymentFormPurse',
                                      )}`,
                                      onClick: () => {
                                          refundPaymentMutation.mutate({
                                              id: idPaymentModal,
                                              params: {
                                                  paymentForm:
                                                      constants.payment
                                                          .paymentForms.purse
                                                          .name,
                                              },
                                          });
                                      },
                                      show: paymentPrivileges.includes(
                                          constants.payment.privileges.refund,
                                      ),
                                  },
                                  {
                                      caption: `${t('generic.refund')} - ${t(
                                          'payment.paymentFormDataphone',
                                      )}`,
                                      onClick: () => {
                                          refundPaymentMutation.mutate({
                                              id: idPaymentModal,
                                              params: {
                                                  paymentForm:
                                                      constants.payment
                                                          .paymentForms
                                                          .dataphone.name,
                                              },
                                          });
                                      },
                                      // 869cydgd4: only when the original
                                      // payment was made with a datáfono.
                                      show:
                                          paymentPrivileges.includes(
                                              constants.payment.privileges
                                                  .refund,
                                          ) &&
                                          isDataphonePayment(
                                              getPaymentQuery.data.payment
                                                  .paymentForm,
                                          ),
                                  },
                                  {
                                      caption: `${t('generic.refund')} - ${t(
                                          'payment.paymentFormTpv',
                                      )}`,
                                      onClick: () => {
                                          refundPaymentMutation.mutate({
                                              id: idPaymentModal,
                                              params: {
                                                  paymentForm:
                                                      constants.payment
                                                          .paymentForms.tpv
                                                          .name,
                                              },
                                          });
                                      },
                                      show: paymentPrivileges.includes(
                                          constants.payment.privileges
                                              .refundTPV,
                                      ),
                                  },
                                  {
                                      caption: t('generic.ticket'),
                                      divider: true,
                                      show: !getPaymentQuery.data.payment
                                          .parent,
                                      onClick: () => ticket(1),
                                  },
                                  {
                                      caption: `${t('generic.ticket')} A4`,
                                      show: !getPaymentQuery.data.payment
                                          .parent,
                                      onClick: () => ticket(0),
                                  },
                                  {
                                      caption: `${t('generic.ticket')} A4 triple`,
                                      show: !getPaymentQuery.data.payment
                                          .parent,
                                      onClick: () => ticket(2),
                                  },
                                  {
                                      caption: t('generic.booking'),
                                      divider: true,
                                      show:
                                          getPaymentQuery.data.payment
                                              .module ===
                                          constants.modules.renting.name,
                                      onClick: () =>
                                          setDialogBookingId(
                                              getPaymentQuery.data.payment
                                                  .booking!.id,
                                          ),
                                  },
                              ]
                            : undefined
                    }
                >
                    {mutationsLoader}
                    <Container>
                        <Box my={3}>
                            {getPaymentQuery.data.payment.trash && (
                                <Alert
                                    severity="warning"
                                    action={
                                        <Button
                                            color="inherit"
                                            onClick={() => {
                                                setOperationConfirmation(
                                                    'recover',
                                                );
                                            }}
                                        >
                                            {t('generic.recover')}
                                        </Button>
                                    }
                                >
                                    {t('payment.deleted')}
                                </Alert>
                            )}

                            {getPaymentQuery.data.payment.refund && (
                                <Alert
                                    severity="info"
                                    action={
                                        <Button
                                            color="inherit"
                                            onClick={() => {
                                                if (
                                                    getPaymentQuery.data.payment
                                                        .refund
                                                ) {
                                                    openPayment(
                                                        getPaymentQuery.data
                                                            .payment.refund,
                                                    );
                                                }
                                            }}
                                        >
                                            {t('payment.payment')}
                                        </Button>
                                    }
                                >
                                    {t('payment.refunded')}
                                </Alert>
                            )}

                            {getPaymentQuery.data.payment.parent && (
                                <Alert
                                    severity="info"
                                    action={
                                        <Button
                                            color="inherit"
                                            onClick={() => {
                                                if (
                                                    getPaymentQuery.data.payment
                                                        .parent
                                                ) {
                                                    openPayment(
                                                        getPaymentQuery.data
                                                            .payment.parent,
                                                    );
                                                }
                                            }}
                                        >
                                            {t('generic.openParent')}
                                        </Button>
                                    }
                                >
                                    {t('payment.isChild')}
                                </Alert>
                            )}

                            <SttTabs
                                tabs={[
                                    {
                                        caption: t('payment.payment'),
                                        component: (
                                            <PaymentModalDetail
                                                payment={
                                                    getPaymentQuery.data.payment
                                                }
                                                onSave={(payment: any) => {
                                                    queryClient.setQueryData<GetPayment200>(
                                                        getPaymentQuery.queryKey,
                                                        () => ({
                                                            payment,
                                                        }),
                                                    );

                                                    onSave?.(payment);
                                                }}
                                            />
                                        ),
                                    },
                                    {
                                        show:
                                            getPaymentQuery.data.payment
                                                .privileges !== undefined &&
                                            getPaymentQuery.data.payment
                                                .children !== null &&
                                            getPaymentQuery.data.payment
                                                .children !== undefined,
                                        caption: t('generic.children'),
                                        component: (
                                            <PaymentModalChildren
                                                payment={
                                                    getPaymentQuery.data
                                                        .payment as any
                                                }
                                                onOpenChild={openPayment}
                                            />
                                        ),
                                    },
                                ]}
                            />
                        </Box>
                    </Container>

                    <PaymentFormDialog
                        open={openSelectPF}
                        onClose={() => setOpenSelectPF(false)}
                        pfs={(
                            [
                                constants.payment.paymentForms.purse.name,
                                constants.payment.paymentForms.cash.name,
                                constants.payment.paymentForms.bank.name,
                                cxt?.sc?.dataphoneConnected
                                    ? constants.payment.paymentForms
                                          .dataphoneConnected.name
                                    : constants.payment.paymentForms.dataphone
                                          .name,
                                constants.payment.paymentForms.receipt.name,
                                constants.payment.paymentForms.free.name,
                            ] as PaymentForm[]
                        ).concat(
                            CASHDRO
                                ? [constants.payment.paymentForms.cashdro.name]
                                : [],
                        )}
                        onSelect={(paymentForm: PaymentForm) => {
                            setupPaymentCollection(paymentForm);
                            setOpenSelectPF(false);
                        }}
                    />

                    {cxt.sc?.sporttiaStripeLocationId &&
                    posPaymentParams !== null ? (
                        <POSStripePaymentDialog
                            paymentData={posPaymentParams}
                            onClose={() => setPosPaymentParams(null)}
                            onSuccess={() => {
                                // No podemos hacer refetch ya que el status en caso de Stripe se actualiza con un cronjob
                                // y se actualiza de forma asíncrona en el backend
                                const optimisticPayment = {
                                    ...getPaymentQuery.data.payment,
                                    status: constants.payment.status.paid
                                        .name as Payment['status'],
                                    privileges: [
                                        ...(
                                            getPaymentQuery.data.payment
                                                .privileges ?? []
                                        ).filter(
                                            (privilege: any) =>
                                                privilege !==
                                                constants.payment.privileges
                                                    .pay,
                                        ),
                                    ],
                                };

                                queryClient.setQueryData<GetPayment200>(
                                    getPaymentQuery.queryKey,
                                    () => ({
                                        payment: optimisticPayment,
                                    }),
                                );

                                onPay?.(optimisticPayment);
                            }}
                        />
                    ) : (
                        <DataphonePaymentDialog
                            open={posPaymentParams !== null}
                            paymentData={posPaymentParams}
                            onClose={() => setPosPaymentParams(null)}
                            onSuccess={(_type: any, returnParams: any) => {
                                onPay?.(returnParams?.payment);
                            }}
                        />
                    )}

                    {cashdroPayment && (
                        <CashdroControlDialog
                            open={cashdroPayment}
                            amount={cashdroPaymentAmount}
                            onClose={() => {
                                setCashdroPayment(false);
                                setCashdroPaymentAmount(0);
                            }}
                            onSuccess={() => {
                                setCashdroPayment(false);
                                setCashdroPaymentAmount(0);
                                collectPayment('CASHDRO');
                            }}
                        />
                    )}
                </SttFullDialog>
            )}

            {operationConfirmation && (
                <ModalConfirmation
                    show
                    onAccept={() => {
                        if (operationConfirmation === 'delete') {
                            deletePaymentMutation.mutate({
                                id: idPaymentModal,
                            });
                        }

                        if (operationConfirmation === 'recover') {
                            recoverPaymentMutation.mutate({
                                id: idPaymentModal,
                            });
                        }

                        setOperationConfirmation(undefined);
                    }}
                    onClose={() => {
                        setOperationConfirmation(undefined);
                    }}
                />
            )}

            {dialogBookingId !== null && (
                <BookingDialog
                    idBooking={dialogBookingId}
                    onClose={() => setDialogBookingId(null)}
                />
            )}
        </>
    );
}
